/** @jsxImportSource @emotion/react */
import { useTranslation } from 'react-i18next';
import { ButtonGroup, Modal, Button, TextField } from '@mui/material';
import PhotoIcon from '@mui/icons-material/Photo';
import DeleteIcon from '@mui/icons-material/Delete';

// @ts-ignore
import './styles.css';

import { styles } from './styles';
import { useEffect, useState } from 'react';
import { usePhotoShare } from '@hooks/usePhotoShare';
import { useSelector } from 'react-redux';
import { RootState } from '@state-mgmt/store';

export interface Props {
  open: boolean;
  photo: any;
  onClose: () => void;
  onPhotoDeleted: (id: string, valueId: string) => void;
  onUpdatePhotoDescription: (id: string, valueId: string, description: string) => void;
}

const PhotoViewModal = ({ open, photo, onClose, onPhotoDeleted, onUpdatePhotoDescription }: Props) => {
  const { t } = useTranslation();

  const [description, setDescription] = useState<string>('');
  const [hasUpdate, setHasUpdate] = useState<boolean>(false);
  const themeMode = useSelector((state: RootState) => state.app.themeMode);

  useEffect(() => {
    if (open) {
      setDescription(photo?.description || '');
      setHasUpdate(false);
    } else {
      setDescription('');
      setHasUpdate(false);
    }
  }, [open, photo?.description]);

  const { savePhoto } = usePhotoShare({ url: photo?.src });

  return photo ? (
    <Modal open={open} onClose={onClose}>
      <div className="photo-view-container">
        <div className="image-container">
          <img src={photo?.src} className="photo" alt="some place" />
        </div>

        <div className="actions-description">
          <TextField
            label="Photo description"
            value={description}
            onChange={e => {
              setHasUpdate(true);
              setDescription(e.target.value);
            }}
            multiline
            rows={3}
            fullWidth
            inputProps={{
              maxLength: 500
            }}
            helperText={`${description.length}/500`}
            sx={
              themeMode === 'light'
                ? {
                    '& .MuiInputBase-input': {
                      color: 'white'
                    },
                    '& .MuiInputLabel-root:not(.Mui-focused)': {
                      color: 'white'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'white'
                    },
                    '& .MuiOutlinedInput-root:hover:not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'white'
                    },
                    '& .MuiFormHelperText-root': {
                      color: 'white'
                    }
                  }
                : {}
            }
          />
        </div>

        <ButtonGroup variant="text" aria-label="outlined primary button group" className="actions">
          {hasUpdate ? (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  onUpdatePhotoDescription(photo.id, photo.valueId, description);
                  onClose();
                }}
              >
                {t('general.save')}
              </Button>
              <Button variant="outlined" onClick={onClose}>
                {t('general.cancel')}
              </Button>
            </>
          ) : (
            <Button variant="outlined" onClick={onClose}>
              {t('general.close')}
            </Button>
          )}
          <Button variant="outlined" onClick={savePhoto} startIcon={<PhotoIcon />}>
            {t('general.download')}
          </Button>

          <Button
            variant="outlined"
            startIcon={<DeleteIcon />}
            color="error"
            onClick={() => {
              onPhotoDeleted(photo.id, photo.valueId);
              onClose();
            }}
          >
            {t('general.delete')}
          </Button>
        </ButtonGroup>
      </div>
    </Modal>
  ) : (
    <></>
  );
};

export default PhotoViewModal;
